import React from 'react';
import parse, { domToReact } from 'html-react-parser';
import { Image } from 'antd';
import ProtectedEmailLink from '../ProtectedEmailLink/ProtectedEmailLink.jsx';
import '../../antd/form/FormTextEditor/editorContent.css';

const HtmlContentWithAntdImages = ({ content }) => {
  // Helper to extract plain text from a dom node (recursive)
  const getNodeText = (node) => {
    if (!node) return '';
    if (node.type === 'text') return node.data || '';
    if (Array.isArray(node.children)) {
      return node.children.map(getNodeText).join('');
    }
  };

  // css prop-name (kebab) -> camelCase
  const camelCaseCss = (prop) =>
    prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

  // Parse inline style string (e.g., "margin-top: 1em; color: red;") into a React style object
  const parseStyleString = (styleStr) => {
    const style = {};
    if (!styleStr || typeof styleStr !== 'string') return style;
    styleStr.split(';').forEach((rule) => {
      const [rawKey, ...rest] = rule.split(':');
      if (!rawKey || rest.length === 0) return;
      const key = camelCaseCss(rawKey.trim());
      const value = rest.join(':').trim();
      if (key && value) style[key] = value;
    });
    return style;
  };

  // Convert attribs from DOM to React props (class -> className, style string -> object)
  const convertAttribs = (attribs = {}) => {
    const out = { ...attribs };
    if (Object.prototype.hasOwnProperty.call(out, 'class')) {
      out.className = out.class;
      delete out.class;
    }
    if (typeof out.style === 'string') {
      out.style = parseStyleString(out.style);
    }
    return out;
  };

  // Render children via domToReact and ensure keys at this level
  const childrenWithKeys = (children) => {
    if (!Array.isArray(children) || children.length === 0) return null;
    return children.map((child, idx) => (
      <React.Fragment
        key={(child && (child.attribs?.id || child.name || child.type)) || idx}
      >
        {domToReact([child], options)}
      </React.Fragment>
    ));
  };

  const options = {
    replace: (domNode) => {
      if (domNode.type !== 'tag') return;

      // Nahradiť <p><img /></p> => <div><Image /></div>
      if (domNode.name === 'p' && domNode.children?.[0]?.name === 'img') {
        const { src, alt, width, height } = domNode.children[0].attribs;
        const attribs = convertAttribs(domNode.attribs || {});
        return (
          <div {...attribs}>
            <Image
              src={src}
              alt={alt || ''}
              width={width}
              height={height}
              preview={{
                scaleStep: 0.2,
                imageRender: (originalNode) => (
                  <div
                    style={{
                      maxWidth: '90vw',
                      maxHeight: '90vh',
                      width: 'auto',
                      height: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                    }}
                  >
                    {React.cloneElement(originalNode, {
                      style: {
                        maxWidth: '90%',
                        maxHeight: '90%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                      },
                    })}
                  </div>
                ),
              }}
            />
          </div>
        );
      }

      // Pridať class fluid-video pre <p> s <iframe> ako child
      if (domNode.name === 'p' && domNode.children?.[0]?.name === 'iframe') {
        const attribs = convertAttribs(domNode.attribs || {});
        attribs.className =
          (attribs.className ? attribs.className + ' ' : '') + 'fluid-video';
        return <p {...attribs}>{childrenWithKeys(domNode.children || [])}</p>;
      }

      // Odstrániť samostatné <img>
      if (domNode.name === 'img') return null;

      // Obnoviť protected email odkazy
      if (
        domNode.name === 'a' &&
        domNode.attribs &&
        domNode.attribs['data-u'] &&
        domNode.attribs['data-d']
      ) {
        const { ['data-u']: user, ['data-d']: domain } = domNode.attribs;
        return (
          <ProtectedEmailLink
            user={user}
            domain={domain}
            text={getNodeText(domNode)}
          />
        );
      }

      // Wrap <table> in <div class="table-responsive"> and ensure keyed children
      if (domNode.name === 'table') {
        const attribs = convertAttribs(domNode.attribs || {});
        return (
          <div className="table-responsive">
            <table {...attribs}>
              {childrenWithKeys(domNode.children || [])}
            </table>
          </div>
        );
      }

      // Ensure <tbody> children have unique keys and proper style
      if (domNode.name === 'tbody') {
        const attribs = convertAttribs(domNode.attribs || {});
        return (
          <tbody {...attribs}>{childrenWithKeys(domNode.children || [])}</tbody>
        );
      }

      // Generic fallback: if element has class or style string, convert attribs and render with keyed children
      if (
        domNode.attribs &&
        (Object.prototype.hasOwnProperty.call(domNode.attribs, 'class') ||
          typeof domNode.attribs.style === 'string')
      ) {
        const attribs = convertAttribs(domNode.attribs);
        return React.createElement(
          domNode.name,
          attribs,
          childrenWithKeys(domNode.children || []),
        );
      }

      return undefined;
    },
  };

  return <div className="tinymce-content">{parse(content, options)}</div>;
};

export default HtmlContentWithAntdImages;
